// ─── Runtime Inspector ────────────────────────────────────────────────────────
//
// High-level runtime introspection. Aggregates state, component, and metrics
// data into a unified RuntimeSnapshot for diagnostics and debugging.
//
// All reads are non-destructive - no runtime state is mutated.

import { getMountedComponentIds } from '@/components/componentRuntime';
import { getMetrics } from './runtimeMetrics';
import { getTimeline, getRecentEntries } from './executionTimeline';
import { inspectState } from './stateInspector';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RuntimeSnapshot {
  timestamp: number;
  uptimeMs: number;
  state: Record<string, unknown>;
  stateKeyCount: number;
  mountedComponents: string[];
  mountedComponentCount: number;
  recentActions: number;
  recentErrors: number;
  eventCount: number;
  timelineEntryCount: number;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Capture a point-in-time snapshot of the full runtime.
 * Suitable for logging, export, or display in a DevTools panel.
 */
export function captureSnapshot(): RuntimeSnapshot {
  const metrics = getMetrics();
  const stateReport = inspectState();
  const mountedComponents = getMountedComponentIds();
  const recentEntries = getRecentEntries(100);
  const timeline = getTimeline();

  const recentActions = recentEntries.filter(e => e.type === 'action').length;
  const recentErrors  = recentEntries.filter(e => e.type === 'error').length;

  return {
    timestamp: Date.now(),
    uptimeMs: metrics.uptimeMs,
    state: stateReport.rawValues,
    stateKeyCount: stateReport.totalEntries,
    mountedComponents,
    mountedComponentCount: mountedComponents.length,
    recentActions,
    recentErrors,
    eventCount: metrics.eventCount,
    timelineEntryCount: timeline.length,
  };
}

/**
 * Returns a human-readable description of the current runtime state.
 */
export function describeRuntime(): string {
  const snap = captureSnapshot();
  const uptime = (snap.uptimeMs / 1000).toFixed(1);

  return [
    `Runtime @ ${new Date(snap.timestamp).toISOString()}`,
    `  Uptime: ${uptime}s`,
    `  State keys: ${snap.stateKeyCount}`,
    `  Mounted components: ${snap.mountedComponentCount} [${snap.mountedComponents.join(', ') || 'none'}]`,
    `  Events processed: ${snap.eventCount}`,
    `  Timeline entries: ${snap.timelineEntryCount}`,
    `  Recent actions: ${snap.recentActions}`,
    `  Recent errors: ${snap.recentErrors}`,
  ].join('\n');
}

/**
 * Check if the runtime is in a healthy state.
 * Returns true if: error rate is low and at least one component is mounted.
 */
export function isRuntimeHealthy(): boolean {
  const metrics = getMetrics();
  const totalOps = metrics.actionCount + metrics.eventCount;
  if (totalOps === 0) return true;
  const errorRate = metrics.errorCount / totalOps;
  return errorRate < 0.1;
}
